import { useState, useEffect } from 'react';          // <-- added useEffect, useState
import { Link, useLocation } from 'react-router-dom';
import { useAuth, API } from '../../context/AuthContext';   // <-- added API

import {
  LayoutDashboard,
  Building2,
  PlusCircle,
  Map,
  MapPin,
  HeartPulse,
  Newspaper,
  FolderOpen,
  Images,
  Users,
  BarChart3,
  Mail,
  ClipboardList,
  Settings,
  LogOut
} from 'lucide-react';

const NAV = [
  { section: 'MAIN' },
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/admin/organisations', icon: Building2, label: 'Organisations' },
  { to: '/admin/organisations/new', icon: PlusCircle, label: 'Add Organisation' },
  { section: 'GEOGRAPHY' },
  { to: '/admin/provinces', icon: Map, label: 'Provinces' },
  { to: '/admin/districts', icon: MapPin, label: 'Districts' },
  { to: '/admin/thematic', icon: HeartPulse, label: 'Thematic Areas' },
  { section: 'CONTENT' },
  { to: '/admin/news', icon: Newspaper, label: 'News & Updates' },
  { to: '/admin/resources', icon: FolderOpen, label: 'Resources' },
  { to: '/admin/media', icon: Images, label: 'Media Library' },
  { section: 'MANAGEMENT' },
  { to: '/admin/users', icon: Users, label: 'Users', roles: ['super_admin', 'admin'] },
  { to: '/admin/reports', icon: BarChart3, label: 'Reports & Analytics' },
  // badge property is now dynamic – we'll remove the static badge from NAV
  { to: '/admin/contact', icon: Mail, label: 'Contact Inbox', badgeKey: 'contact' },
  { to: '/admin/audit', icon: ClipboardList, label: 'Audit Logs', roles: ['super_admin', 'admin'] },
  { to: '/admin/settings', icon: Settings, label: 'Settings', roles: ['super_admin'] }
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);   // <-- new state for badge

  // Fetch unread message count on mount
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await API.get('/contact');
        const msgs = res.data;                   // array of all messages
        const count = msgs.filter(m => !m.is_read).length;
        setUnreadCount(count);
      } catch (err) {
        console.error('Failed to fetch unread count', err);
      }
    };
    fetchUnread();
    // Optionally refresh every 60 seconds
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, []);

  const isActive = (to) => {
    if (to === '/admin') return pathname === '/admin';
    if (to === '/admin/organisations') return pathname === '/admin/organisations';
    return pathname.startsWith(to);
  };

  const canSee = (item) => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role);
  };

  const initials = (user?.full_name || 'ZA')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <aside className="admin-sidebar">
      {/* Logo */}
      <div className="sidebar-logo">
        <div className="sidebar-logo-mark">ZAN</div>
        <div className="sidebar-logo-text">
          <div className="sidebar-logo-title">Zim CSO Database</div>
          <div className="sidebar-logo-sub">Admin Panel</div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {NAV.map((item, index) => {
          if (item.section) {
            return (
              <div key={index} className="sidebar-section-label">
                {item.section}
              </div>
            );
          }

          if (!canSee(item)) return null;

          const Icon = item.icon;
          // Show badge only for Contact Inbox when unreadCount > 0
          const showBadge = item.badgeKey === 'contact' && unreadCount > 0;

          return (
            <Link
              key={item.to}
              to={item.to}
              className={`sidebar-nav-item ${isActive(item.to) ? 'active' : ''}`}
            >
              <span className="nav-icon">
                <Icon size={18} />
              </span>
              <span className="nav-label">{item.label}</span>
              {showBadge && (
                <span className="sidebar-nav-badge">
                  {unreadCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        <div className="sidebar-avatar">{initials}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="sidebar-footer-name" style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {user?.full_name}
          </div>
          <div className="sidebar-footer-role">{user?.role?.replace('_', ' ')}</div>
        </div>
        <button onClick={logout} title="Logout" className="sidebar-logout-btn">
          <LogOut size={18} />
        </button>
      </div>
    </aside>
  );
}